import React, { useCallback, useMemo, useState } from 'react';
import { Calendar, Card, Spin } from 'antd';
import moment from 'moment';
import { ToolOutlined, CarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { formatDuration } from '../../utils/WrenchModeEventUtils';
import { WrenchHubUserAnalytic } from '../../thunks/wrench_mode_events_thunks';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';

export interface DateRange {
	from: moment.Moment;
	to: moment.Moment;
}

interface WrenchModeLogsSidebarProps {
	dateRange: DateRange;
	onRangeChange: (range: DateRange) => void;
	techAnalytics: WrenchHubUserAnalytic[];
	loading: boolean;
	selectedTechnicianEmail?: string;
	onTechnicianSelect?: (email: string) => void;
}

const formatRangeTitle = (range: DateRange): string => {
	const fmt = 'D MMM, YYYY';
	if (range.from.isSame(range.to, 'day')) {
		return range.from.format(fmt);
	}
	return `${range.from.format(fmt)} - ${range.to.format(fmt)}`;
};

const orderedRange = (a: moment.Moment, b: moment.Moment): [moment.Moment, moment.Moment] => {
	return a.isSameOrBefore(b, 'day') ? [a.clone(), b.clone()] : [b.clone(), a.clone()];
};

const isFullMonthRange = (range: DateRange): boolean => {
	return range.from.date() === 1 && range.to.isSame(range.from.clone().endOf('month'), 'day');
};

const WrenchModeLogsSidebar: React.FC<WrenchModeLogsSidebarProps> = ({
	dateRange,
	onRangeChange,
	techAnalytics,
	loading,
	selectedTechnicianEmail,
	onTechnicianSelect,
}) => {
	const [viewDate, setViewDate] = useState<moment.Moment>(dateRange.from.clone());
	// First click of a pending range; null once the range has been committed.
	const [anchor, setAnchor] = useState<moment.Moment | null>(null);
	const [hovered, setHovered] = useState<moment.Moment | null>(null);

	const sortedAnalytics = useMemo(
		() => [...techAnalytics].sort((a, b) => (b.totalTimeMs || 0) - (a.totalTimeMs || 0)),
		[techAnalytics]
	);

	const emitFullMonth = useCallback(
		(refDate: moment.Moment) => {
			const fullMonth = {
				from: refDate.clone().startOf('month'),
				to: refDate.clone().endOf('month'),
			};
			setAnchor(null);
			setHovered(null);
			onRangeChange(fullMonth);
		},
		[onRangeChange]
	);

	const handleSelect = useCallback(
		(date: moment.Moment) => {
			const clicked = date.clone().startOf('day');
			setViewDate(clicked.clone());

			if (!anchor) {
				const isSameAsCurrent =
					dateRange.from.isSame(clicked, 'day') && dateRange.to.isSame(clicked, 'day');
				if (isSameAsCurrent && !isFullMonthRange(dateRange)) {
					emitFullMonth(clicked);
					return;
				}
				setAnchor(clicked);
				onRangeChange({ from: clicked.clone(), to: clicked.clone() });
				return;
			}

			if (anchor.isSame(clicked, 'day')) {
				emitFullMonth(clicked);
				return;
			}

			const [from, to] = orderedRange(anchor, clicked);
			setAnchor(null);
			setHovered(null);
			onRangeChange({ from, to });
		},
		[anchor, onRangeChange, dateRange, emitFullMonth]
	);

	// While a range is mid-selection (anchor set, awaiting second click), preview
	// runs from anchor to the hovered cell. Otherwise use the committed dateRange.
	const previewRange: [moment.Moment, moment.Moment] =
		anchor && hovered ? orderedRange(anchor, hovered) : [dateRange.from, dateRange.to];
	const isSingleDay = previewRange[0].isSame(previewRange[1], 'day');

	const renderCell = (current: moment.Moment) => {
		const isOtherMonth = !current.isSame(viewDate, 'month');
		const hoverHandler = anchor ? () => setHovered(current.clone().startOf('day')) : undefined;
		if (isOtherMonth) {
			return (
				<div className="rangeCell" onMouseEnter={isOtherMonth ? hoverHandler : undefined}>
					<span className="rangeCell__date text-character-disabled">{current.date()}</span>
				</div>
			);
		}

		const inRange =
			current.isSameOrAfter(previewRange[0], 'day') &&
			current.isSameOrBefore(previewRange[1], 'day');
		const isStart = current.isSame(previewRange[0], 'day');
		const isEnd = current.isSame(previewRange[1], 'day');
		const isToday = current.isSame(moment(), 'day');

		let outerCls = 'rangeCell';
		if (inRange && !isSingleDay) {
			outerCls += ' rangeCell--band';
			if (isStart) outerCls += ' rangeCell--start';
			if (isEnd) outerCls += ' rangeCell--end';
		}

		let dateCls = 'rangeCell__date';
		if (inRange && (isStart || isEnd || isSingleDay)) {
			dateCls += ' rangeCell__date--edge';
		} else if (isToday) {
			dateCls += ' rangeCell__date--today';
		}

		return (
			<div className={outerCls} onMouseEnter={hoverHandler}>
				<span className={dateCls}>{current.date()}</span>
			</div>
		);
	};

	return (
		<div className="wrenchModeLogsSidebar flex w-1/4 flex-none flex-col gap-s overflow-y-auto border-r border-grey-4">
			<div onMouseLeave={() => setHovered(null)}>
				<Calendar
					fullscreen={false}
					value={viewDate}
					onSelect={handleSelect}
					onPanelChange={(date) => setViewDate(date.clone())}
					dateFullCellRender={renderCell}
					headerRender={({ value }) => {
						return (
							<div className="flex items-center justify-between px-3 py-2">
								<LeftOutlined
									className="cursor-pointer text-character-secondary"
									onClick={() => {
										const prev = value.clone().subtract(1, 'month');
										setViewDate(prev.clone().startOf('month'));
										emitFullMonth(prev);
									}}
								/>
								<span className="h5medium text-character-primary">{value.format('MMM YYYY')}</span>
								<RightOutlined
									className="cursor-pointer text-character-secondary"
									onClick={() => {
										const next = value.clone().add(1, 'month');
										setViewDate(next.clone().startOf('month'));
										emitFullMonth(next);
									}}
								/>
							</div>
						);
					}}
				/>
			</div>
			<Card
				title={'Summary: ' + formatRangeTitle(dateRange)}
				className="flex-1 border-t border-grey-4 bg-white"
				headStyle={{ padding: '12px 16px', borderBottomColor: '#f0f0f0' }}
				bodyStyle={{ padding: '12px 16px' }}
			>
				{loading && sortedAnalytics.length === 0 ? (
					<div className="flex justify-center p-m">
						<Spin />
					</div>
				) : sortedAnalytics.length === 0 ? (
					<p className="footnoteregular  text-character-secondary">
						No activity for this date range
					</p>
				) : (
					<div className="flex flex-col gap-s">
						{sortedAnalytics.map((tech) => {
							const isSelected = tech.supplierEmail === selectedTechnicianEmail;
							return (
								<div
									key={tech.supplierEmail}
									role="button"
									tabIndex={0}
									aria-pressed={isSelected}
									onClick={() => onTechnicianSelect && onTechnicianSelect(tech.supplierEmail)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											onTechnicianSelect && onTechnicianSelect(tech.supplierEmail);
										}
									}}
									className={`flex w-full cursor-pointer flex-wrap items-center gap-1 border-b border-grey-5 px-2 py-2 transition-colors last:border-b-0 hover:bg-grey-3 ${
										isSelected ? 'bg-[#eaf8fb] hover:bg-[#eaf8fb]' : ''
									}`}
								>
									<div className="flex min-w-0 flex-auto items-center">
										<UserNameWithEmail
											showNameWithAvatar
											mode="inline"
											email={tech.supplierEmail}
										/>
									</div>
									<div className="ml-auto flex flex-col items-end justify-center gap-1">
										<span className="bodyregular text-character-primary">
											{formatDuration(tech.totalTimeMs || 0)}
										</span>
										<div className="flex items-start gap-xs">
											<span className="footnoteregular flex items-center gap-1 text-grey-8">
												<ToolOutlined style={{ fontSize: 12 }} />
												{formatDuration(tech.workTimeMs || 0)}
											</span>
											<span className="footnoteregular flex items-center gap-1 text-grey-8">
												<CarOutlined style={{ fontSize: 12 }} />
												{formatDuration(tech.driveTimeMs || 0)}
											</span>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</Card>
		</div>
	);
};

export default WrenchModeLogsSidebar;
